import { useEffect, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import AppStoreBadges from "@/components/landing/AppStoreBadges";
import { btnLift, focusRing } from "@/components/home/styles";
import { trackEvent } from "@/lib/analytics";
import logo from "@/assets/logo-dark.webp";

// React 18 only knows the lowercase DOM attribute.
const HIGH_PRIORITY = { fetchpriority: "high" } as Record<string, string>;

type NavItem = { label: string; hash?: string; to?: string };

interface NavLinkProps {
  item: NavItem;
  onHome: boolean;
  className: string;
  onClick?: () => void;
  children: ReactNode;
}

/**
 * Home sections: a plain "#id" anchor on "/" (native smooth scroll), and a
 * router link to "/#id" from other pages (the home page scrolls to the hash after render).
 */
const NavLink = ({ item, onHome, className, onClick, children }: NavLinkProps) => {
  if (item.hash) {
    return onHome ? (
      <a href={`#${item.hash}`} className={className} onClick={onClick}>
        {children}
      </a>
    ) : (
      <Link to={`/#${item.hash}`} className={className} onClick={onClick}>
        {children}
      </Link>
    );
  }
  return (
    <Link to={item.to ?? "/"} className={className} onClick={onClick}>
      {children}
    </Link>
  );
};

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const onHome = pathname === "/";

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  // Close the menu after any route change.
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  const navLinks: NavItem[] = [
    { label: t("header.features"), hash: "features" },
    { label: t("home.nav.plans"), hash: "plans" },
    { label: t("footer.blog"), to: "/blog" },
    { label: t("home.nav.tools"), to: "/tools" },
    { label: t("header.faq"), hash: "faq" },
  ];

  const closeMenu = () => setIsMobileMenuOpen(false);
  const getAppItem: NavItem = { label: t("header.getApp"), hash: "download" };

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-foreground/10 bg-background/85 backdrop-blur-xl backdrop-saturate-150">
      <nav
        aria-label={t("home.nav.main")}
        className="mx-auto flex h-[60px] max-w-[1200px] items-center justify-between gap-6 px-[22px]"
      >
        <Link
          to="/"
          className={`flex min-h-[44px] items-center gap-[9px] rounded-lg text-foreground ${focusRing}`}
          dir="ltr"
        >
          <img
            src={logo}
            alt="PieFolio AI"
            width="90"
            height="125"
            className="block h-7 w-auto"
            {...HIGH_PRIORITY}
            decoding="async"
          />
          <span className="text-[19px] font-semibold tracking-[-0.02em]">PieFolio</span>
        </Link>

        {/* Desktop links */}
        <ul className="m-0 hidden list-none items-center gap-8 p-0 text-sm font-medium lg:flex">
          {navLinks.map((item) => (
            <li key={item.label}>
              <NavLink
                item={item}
                onHome={onHome}
                className={`inline-flex min-h-[44px] items-center rounded-md text-foreground transition-colors hover:text-primary ${focusRing}`}
              >
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2 sm:gap-3.5">
          <LanguageSwitcher />
          <NavLink
            item={getAppItem}
            onHome={onHome}
            onClick={() => trackEvent("cta_click", { source: "header_get_app" })}
            className={`hidden h-10 items-center rounded-full bg-primary px-[18px] text-sm font-semibold text-white sm:inline-flex ${btnLift} ${focusRing}`}
          >
            {t("header.getApp")}
          </NavLink>
          <button
            type="button"
            className={`flex h-11 w-11 items-center justify-center rounded-full text-foreground transition-colors hover:bg-foreground/5 lg:hidden ${focusRing}`}
            onClick={() => setIsMobileMenuOpen((open) => !open)}
            aria-label={isMobileMenuOpen ? t("home.nav.closeMenu") : t("home.nav.openMenu")}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
          </button>
        </div>
      </nav>

      {/* Mobile / tablet menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            id="mobile-menu"
            className="overflow-hidden border-t border-foreground/10 bg-background lg:hidden"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <ul className="mx-auto flex max-w-[1200px] list-none flex-col gap-1 px-[22px] pb-6 pt-3">
              {navLinks.map((item) => (
                <li key={item.label}>
                  <NavLink
                    item={item}
                    onHome={onHome}
                    onClick={closeMenu}
                    className={`flex min-h-[48px] items-center rounded-xl px-4 text-base font-medium text-foreground transition-colors hover:bg-foreground/5 ${focusRing}`}
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
              <li className="mt-3 px-1">
                <AppStoreBadges variant="pill" align="start" location="mobile_menu" utmCampaign={onHome ? "home_clarity" : "site_menu"} />
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
